import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import RequirementTab from './RequirementTab';
import SupportingDocumentTab from './SupportingDocumentTab';
import {
  buildDeliveryItemsByTermIdFromBidder,
  buildOfferFormViewState,
  buildQtyDeliveryItemsFromBidder,
  displayValue,
} from '../../../../../utils/tenderEvaluationHelpers';
import {
  mapResponsesByClauseId,
  normalizeRespondClauseDetail,
  toToleranceValidityHistoryItems,
  TYPE_RESPONSE_TOLERANCE,
  TYPE_RESPONSE_VALIDITY,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
} from '../../../../../../../shared/offer-domain/respond';
import useClarificationToleranceValidityResponses from '../../../../../../../shared/offer-domain/respond/useClarificationToleranceValidityResponses';

const ModalOffer = ({ isOpen, toggle, bidder, procurement }) => {
  const [activeTab, setActiveTab] = useState(1);

  const procurementId = procurement?.id;
  const token = getAuthToken();

  const form = useMemo(
    () => buildOfferFormViewState(procurement),
    [procurement]
  );

  const qtyDeliveryData = useMemo(
    () => buildQtyDeliveryItemsFromBidder(bidder),
    [bidder]
  );

  const deliveryItemsByTermId = useMemo(
    () => buildDeliveryItemsByTermIdFromBidder(bidder),
    [bidder]
  );

  const clauseResponses = useMemo(
    () =>
      mapResponsesByClauseId(
        (bidder?.respondClauseDetails ?? [])
          .map(normalizeRespondClauseDetail)
          .filter(Boolean)
      ),
    [bidder?.respondClauseDetails]
  );

  const { responses: typeResponses } =
    useClarificationToleranceValidityResponses(
      token,
      procurementId,
      bidder?.id,
      true
    );

  const typeResponseHistory = useMemo(
    () => ({
      [TYPE_RESPONSE_TOLERANCE]: toToleranceValidityHistoryItems(
        typeResponses[TYPE_RESPONSE_TOLERANCE]
      ),
      [TYPE_RESPONSE_VALIDITY]: toToleranceValidityHistoryItems(
        typeResponses[TYPE_RESPONSE_VALIDITY]
      ),
      [TYPE_RESPONSE_GLOBAL]: typeResponses[TYPE_RESPONSE_GLOBAL] ?? [],
      [TYPE_RESPONSE_COUNTER_PAYMENT_TERM]:
        typeResponses[TYPE_RESPONSE_COUNTER_PAYMENT_TERM] ?? [],
      [TYPE_RESPONSE_COUNTER_COMPLIANCE]:
        typeResponses[TYPE_RESPONSE_COUNTER_COMPLIANCE] ?? [],
    }),
    [typeResponses]
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveTab(1);
    }
  }, [isOpen]);

  if (!bidder) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      style={{ maxWidth: '90%' }}
      backdrop='static'
      scrollable
    >
      <ModalHeader toggle={toggle}>
        Detail Offer - {displayValue(bidder.idOffer)}
      </ModalHeader>
      <ModalBody>
        <Card className='border-0 shadow-none mb-0'>
          <CardBody className='checkout-tab p-0'>
            <Nav tabs className='nav-tabs-custom nav-primary mt-1 mb-3'>
              <NavItem>
                <NavLink
                  className={classnames('cursor-pointer', {
                    active: activeTab === 1,
                  })}
                  onClick={() => setActiveTab(1)}
                >
                  <i className='ri-store-2-fill me-1 align-bottom'></i>
                  <span className='fw-semibold'>Input Offering</span>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames('cursor-pointer', {
                    active: activeTab === 2,
                  })}
                  onClick={() => setActiveTab(2)}
                >
                  <i className='ri-file-text-line me-1 align-bottom'></i>
                  <span className='fw-semibold'>Document Attachment</span>
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId={1} id='pills-bill-info'>
                <RequirementTab
                  form={form}
                  procurement={procurement}
                  procurementMitraId={bidder?.id}
                  qtyDeliveryData={qtyDeliveryData}
                  clauseResponses={clauseResponses}
                  deliveryItemsByTermId={deliveryItemsByTermId}
                  typeResponses={typeResponseHistory}
                />
              </TabPane>
              <TabPane tabId={2}>
                <SupportingDocumentTab
                  procurementId={procurementId}
                  token={token}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default ModalOffer;
